import React from 'react';
import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { Textfield, MixPanelTrackedLink, EmptyDataDiv, MuiAvatar } from '..';
import { useDispatch, useSelector } from 'react-redux';
import { changeUsersWithoutLeadList, fundRaiserPageAction } from '../../store/actions';
import { useState } from 'react';
import { isValidEmail } from '../../helpers';
import { ModalLayout } from './BigModalLayout';
import { useNonInitialEffect } from '../../hooks/UseNonInitialEffect';
import { StyledTeamMemberBox } from './styles/InviteTeamMemberStyles';
import { S3_BUCKET } from '../../constants';

export function InviteTeamMemberModal({ openModal, setOpenModal }) {
	const dispatch = useDispatch();
	const [searchString, setSearchString] = useState('');
	const [searchStringIsEmail, setIsSearchStringEmail] = useState(false);
	const [fetchingUsers, setFetchingUsers] = useState(false);
	const [inviteButtonText, setInviteButtonText] = useState('Send Invitation');
	const user = useSelector((state) => state.authentication.user);
	const pageDetails = useSelector((state) => state.fundraiser_page.fundraiser_details);
	const fetchedUsers = useSelector((state) => state.get_users_except_lead.users_except_lead);

	const fetchUsers = async (searchString) => {
		const payload = {
			user_id: user.id,
			fr_page_id: pageDetails.id,
			search_string: searchString,
		};
		await dispatch(fundRaiserPageAction.fetchUsersToInvite(payload));
	};

	const searchUsers = async (event) => {
		setIsSearchStringEmail(isValidEmail(event.target.value));
		let value = event.target.value.split(' ').join('');
		setSearchString(value);

		if (value.length > 7) {
			setFetchingUsers(true);
			await fetchUsers(value);
			setFetchingUsers(false);
		}
	};

	const sendInviteByEmail = async () => {
		if (searchString && isValidEmail(searchString) && inviteButtonText !== 'Invited') {
			let payload = {
				invited_by: user.id,
				fundraiser_page_id: pageDetails.id,
				email: searchString,
			};
			await dispatch(fundRaiserPageAction.invitationToNewUser(payload));
			setInviteButtonText('Invited');
		}
	};

	const mapUsersIfExist = () => {
		if (fetchedUsers) {
			return fetchedUsers.length ? (
				<Box className="UserCardContainer">
					{fetchedUsers.map((user) => {
						return <UserCardToInvite user={user} callBack={() => fetchUsers(searchString)} />;
					})}
				</Box>
			) : (
				!fetchedUsers.length && !searchStringIsEmail && <EmptyDataDiv message="No member found by this name" />
			);
		}
	};

	useNonInitialEffect(() => {
		if (!openModal) dispatch(changeUsersWithoutLeadList());
	}, [openModal]);

	return (
		<ModalLayout open={openModal} title="Invite Team Member" setOpen={setOpenModal}>
			<StyledTeamMemberBox>
				<Typography align="center">
					Enter the email address of an existing supporter or invite new supporters to sign up by sending them an invitation email.
				</Typography>

				<Stack className="UsersList" gap={2}>
					<Textfield label="Enter email address" margin="normal" onChange={searchUsers} fullWidth />
					{fetchingUsers ? <CircularProgress /> : mapUsersIfExist()}

					{fetchedUsers && !fetchedUsers.length && searchStringIsEmail ? (
						<Stack className="InviteByEmailStack" flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
							<Typography variant="small">This email is not registered on our platform yet. Do you want to send an invitation to join?</Typography>
							<Box className="InviteByEmailButtonBox">
								<MixPanelTrackedLink
									type="button"
									label="Send Invitation"
									context="Invitation to join"
									variant="outlined"
									onClick={sendInviteByEmail}
								>
									{inviteButtonText}
								</MixPanelTrackedLink>
							</Box>
						</Stack>
					) : null}
				</Stack>
			</StyledTeamMemberBox>
		</ModalLayout>
	);
}

function UserCardToInvite({ user, callBack }) {
	const dispatch = useDispatch();
	const pageDetails = useSelector((state) => state.fundraiser_page.fundraiser_details);
	const [invitingUserInProgress, setInvitingUserInProgress] = useState(false);

	const inviteMember = async () => {
		if (!user.is_member) {
			setInvitingUserInProgress(true);
			let payload = {
				fk_user: user.id,
				fk_fundraiser_page: pageDetails.id,
				fk_currency: pageDetails.currency_id,
				status: 1,
				is_leader: 0,
			};
			await dispatch(fundRaiserPageAction.createTeamMember(payload));
			await callBack();
			setInvitingUserInProgress(false);
		}
	};

	return (
		<React.Fragment>
			<Stack className="UserCard">
				<Stack className="UserProfile">
					<MuiAvatar src={S3_BUCKET + user.profile_image} fullName={user.first_name + ' ' + user.last_name} />
					<Typography variant="small">
						{user.first_name}&nbsp; {user.last_name}
					</Typography>
				</Stack>
				<MixPanelTrackedLink
					type="button"
					label="Invite"
					context="Inviting member to team"
					variant={user.is_member ? 'contained' : 'outlined'}
					onClick={inviteMember}
					disabled={invitingUserInProgress}
					sx={{ minWidth: '80px' }}
				>
					{invitingUserInProgress ? <CircularProgress size={25} /> : user.is_member ? 'Invited' : 'Invite'}
				</MixPanelTrackedLink>
			</Stack>
			<Divider />
		</React.Fragment>
	);
}
